import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { processFile, formatFileSize, checkFileSize } from '../../utils/fileCompression'
import { API_BASE_URL } from '../../config/api'

interface Partner {
  id: number
  name: string
  logo: string
  logo_path?: string
  logo_url?: string
  website?: string
  description?: string
  display_order?: number
  is_active?: number
  created_at?: string
  updated_at?: string
}

const PartnersManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const [formData, setFormData] = useState({
    name: '',
    logo: null as File | null,
    website: '',
    description: '',
    display_order: 1,
    is_active: 1
  })


  // Helper function to get full logo URL
  const getLogoUrl = (partner: Partner): string | null => {
    if (partner.logo_url) return partner.logo_url
    if (partner.logo_path) {
      const cleanPath = partner.logo_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    if (partner.logo && partner.logo.startsWith('http')) return partner.logo
    if (partner.logo) {
      const cleanPath = partner.logo.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return null
  }

  // Fetch partners
  const fetchPartners = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}partner_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Sort by display_order if available
        const sortedPartners = result.data.sort((a: Partner, b: Partner) => {
          const orderA = a.display_order || 999
          const orderB = b.display_order || 999
          return orderA - orderB
        })
        setPartners(sortedPartners)
      } else {
        setError(result.message || 'Failed to fetch partners')
        setPartners([])
      }
    } catch (err) {
      setError('Failed to fetch partners. Please try again.')
      console.error('Error fetching partners:', err)
      setPartners([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPartners()
  }, [])

  // Handle logo file selection
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return

    const file = e.target.files[0]
    setError('')
    setUploadProgress('')

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      setError('Logo must be an image file')
      e.target.value = ''
      return
    }

    // Check file size
    const sizeCheck = checkFileSize(file)
    if (!sizeCheck.valid) {
      setError(sizeCheck.message || 'File is too large')
      e.target.value = ''
      return
    }

    setUploadProgress(`Processing logo (${formatFileSize(file.size)})...`)

    // Process and compress file
    const result = await processFile(file)
    
    if (result.error) {
      setError(result.error)
      e.target.value = ''
      return
    }

    setFormData({ ...formData, logo: result.file })
    setUploadProgress(`Logo ready: ${formatFileSize(result.file.size)}`)
  }

  // Handle create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!formData.name.trim()) {
      setError('Partner name is required')
      return
    }

    if (!formData.logo) {
      setError('Partner logo is required')
      return
    }

    setUploading(true)
    setUploadProgress('Uploading partner...')

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name)
      formDataToSend.append('logo', formData.logo)
      
      if (formData.website) {
        formDataToSend.append('website', formData.website)
      }
      if (formData.description) {
        formDataToSend.append('description', formData.description)
      }
      formDataToSend.append('display_order', formData.display_order.toString())
      formDataToSend.append('is_active', formData.is_active.toString())

      const response = await fetch(`${API_BASE_URL}partner_create.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Partner created successfully!')
        resetForm()
        setShowAddModal(false)
        setUploadProgress('')
        fetchPartners()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to create partner')
      }
    } catch (err) {
      setError('Failed to create partner. Please try again.')
      console.error('Error creating partner:', err)
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  // Handle update
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingPartner) return

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('id', editingPartner.id.toString())
      formDataToSend.append('name', formData.name)
      
      // Only add logo if a new one is selected
      if (formData.logo) {
        const sizeCheck = checkFileSize(formData.logo)
        if (!sizeCheck.valid) {
          setError(sizeCheck.message || 'Logo file is too large')
          setUploading(false)
          return
        }
        formDataToSend.append('logo', formData.logo)
      }
      
      if (formData.website !== undefined) {
        formDataToSend.append('website', formData.website)
      }
      if (formData.description !== undefined) {
        formDataToSend.append('description', formData.description)
      }
      formDataToSend.append('display_order', formData.display_order.toString())
      formDataToSend.append('is_active', formData.is_active.toString())

      const response = await fetch(`${API_BASE_URL}partner_update.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Partner updated successfully!')
        setShowEditModal(false)
        setEditingPartner(null)
        resetForm()
        fetchPartners()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to update partner')
      }
    } catch (err) {
      setError('Failed to update partner. Please try again.')
      console.error('Error updating partner:', err)
    } finally {
      setUploading(false)
    }
  }

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this partner? This action cannot be undone.')) {
      return
    }

    setError('')
    setSuccess('')

    try {
      const response = await fetch(`${API_BASE_URL}partner_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Partner deleted successfully!')
        fetchPartners()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete partner')
      }
    } catch (err) {
      setError('Failed to delete partner. Please try again.')
      console.error('Error deleting partner:', err)
    }
  }

  // Open edit modal
  const openEditModal = (partner: Partner) => {
    setEditingPartner(partner)
    setFormData({
      name: partner.name,
      logo: null,
      website: partner.website || '',
      description: partner.description || '',
      display_order: partner.display_order || 1,
      is_active: partner.is_active ?? 1
    })
    setUploadProgress('')
    setError('')
    setShowEditModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      logo: null,
      website: '',
      description: '',
      display_order: 1,
      is_active: 1
    })
    setError('')
    setUploadProgress('')
  }

  // Filter partners based on active status
  const filteredPartners = filter === 'all' 
    ? partners 
    : filter === 'active' 
    ? partners.filter(p => (p.is_active ?? 1) === 1)
    : partners.filter(p => (p.is_active ?? 1) === 0)

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Partners Management</h1>
              <button
                onClick={() => {
                  resetForm()
                  setShowAddModal(true)
                }}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add New Partner</span>
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg animate-fadeIn">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'all'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                All Partners ({partners.length})
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'active'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Active ({partners.filter(p => (p.is_active ?? 1) === 1).length})
              </button>
              <button
                onClick={() => setFilter('inactive')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'inactive'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Inactive ({partners.filter(p => (p.is_active ?? 1) === 0).length})
              </button>
            </div>

            {/* Partners Grid */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">Loading partners...</p>
              </div>
            ) : filteredPartners.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-400 text-lg">
                  {filter === 'all' 
                    ? 'No partners found. Add your first partner!' 
                    : filter === 'active'
                    ? 'No active partners found.'
                    : 'No inactive partners found.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredPartners.map((partner) => {
                  const logoUrl = getLogoUrl(partner)
                  return (
                    <div 
                      key={partner.id} 
                      className={`bg-gray-800 rounded-lg border overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                        (partner.is_active ?? 1) === 1
                          ? 'border-gray-700'
                          : 'border-red-500/50 opacity-75'
                      }`}
                    >
                      <div className="p-6 flex flex-col items-center">
                        {/* Logo */}
                        <div className="w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center mb-4 overflow-hidden">
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={partner.name}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(partner.name)}&background=2563EB&color=fff&size=128`
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-500">
                              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Partner Info */}
                        <h3 className="text-white font-bold text-lg mb-2 text-center">{partner.name}</h3>
                        
                        {partner.description && (
                          <p className="text-gray-400 text-sm mb-3 text-center line-clamp-2">{partner.description}</p>
                        )}

                        {partner.website && (
                          <a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-light hover:text-primary-dark text-sm mb-3 flex items-center space-x-1"
                          >
                            <span>Visit Website</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}

                        {/* Status Badge */}
                        <div className="flex items-center space-x-2 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            (partner.is_active ?? 1) === 1
                              ? 'bg-green-500 text-white'
                              : 'bg-red-500 text-white'
                          }`}>
                            {(partner.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                          </span>
                          {partner.display_order !== undefined && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-600 text-gray-300">
                              Order: {partner.display_order}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-2 w-full">
                          <button
                            onClick={() => openEditModal(partner)}
                            className="flex-1 px-3 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(partner.id)}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Partner Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add New Partner</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-white font-semibold mb-2">
                    Partner Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="Enter partner name"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="logo" className="block text-white font-semibold mb-2">
                    Partner Logo * (Max 50MB)
                  </label>
                  <input
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                  {uploadProgress && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.logo && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-gray-700 rounded">
                      <p className="text-sm text-green-400">
                        {formData.logo.name} ({formatFileSize(formData.logo.size)})
                      </p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo: null })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="website" className="block text-white font-semibold mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="https://www.example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-white font-semibold mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light resize-none"
                    placeholder="Enter partner description"
                  />
                </div>

                <div>
                  <label htmlFor="display_order" className="block text-white font-semibold mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    id="display_order"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    min="1"
                  />
                </div>

                <div>
                  <label htmlFor="is_active" className="block text-white font-semibold mb-2">
                    Active Status *
                  </label>
                  <select
                    id="is_active"
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Creating...' : 'Create Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Partner Modal */}
      {showEditModal && editingPartner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Partner</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingPartner(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="edit-name" className="block text-white font-semibold mb-2">
                    Partner Name *
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-logo" className="block text-white font-semibold mb-2">
                    Update Logo (Optional, Max 50MB)
                  </label>
                  <input
                    type="file"
                    id="edit-logo"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                  {uploadProgress && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.logo && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-gray-700 rounded">
                      <p className="text-sm text-green-400">
                        New: {formData.logo.name} ({formatFileSize(formData.logo.size)})
                      </p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo: null })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {!formData.logo && getLogoUrl(editingPartner) && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-400 mb-2">Current logo:</p>
                      <img
                        src={getLogoUrl(editingPartner) || ''}
                        alt={editingPartner.name}
                        className="w-32 h-32 object-contain bg-gray-700 rounded-lg p-2"
                      />
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-website" className="block text-white font-semibold mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="edit-website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-description" className="block text-white font-semibold mb-2">
                    Description
                  </label>
                  <textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light resize-none"
                  />
                </div>

                <div>
                  <label htmlFor="edit-display_order" className="block text-white font-semibold mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    id="edit-display_order"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    min="1"
                  />
                </div>

                <div>
                  <label htmlFor="edit-is_active" className="block text-white font-semibold mb-2">
                    Active Status *
                  </label>
                  <select
                    id="edit-is_active"
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingPartner(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Updating...' : 'Update Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default PartnersManagement

